/**
 * ONTHEPITCH — I18n Context (react-i18next wrapper)
 * Preserves the legacy `useI18n()` API: `{ t, lang, changeLang }`
 * so existing pages keep working unchanged. New code can also use
 * `useTranslation()` directly from `react-i18next`.
 *
 * Backend sync:
 *  - On login, server sends `user.language` — we hydrate `i18n.language`.
 *  - On switch, we PUT /api/auth/profile {language} for logged-in users.
 *  - For guests, the choice is persisted to localStorage["lang"] only.
 */
import React, { createContext, useContext, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import i18n, { SUPPORTED_LANG_CODES, SUPPORTED_LANGUAGES, DEFAULT_LANG } from "../i18n";
import api from "../lib/api";

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const { t, i18n: i18nInstance } = useTranslation();
  const lang = (i18nInstance.language || DEFAULT_LANG).slice(0, 2);

  const changeLang = useCallback(async (next) => {
    const code = SUPPORTED_LANG_CODES.includes(next) ? next : DEFAULT_LANG;
    await i18nInstance.changeLanguage(code);
    try { localStorage.setItem("lang", code); } catch (_) {}
    // Sync to backend if authenticated
    const token = (typeof window !== "undefined" && localStorage.getItem("auth_token")) || null;
    if (token) {
      api.put("/auth/profile", { language: code }).catch(() => { /* non-blocking */ });
    }
  }, [i18nInstance]);

  // Hydrate language from the authenticated user on first load (best-effort).
  // Only fires when an auth_token is present — avoids 401 noise for guests.
  useEffect(() => {
    const token = (typeof window !== "undefined" && localStorage.getItem("auth_token")) || null;
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled || !data?.language) return;
        if (data.language !== i18nInstance.language && SUPPORTED_LANG_CODES.includes(data.language)) {
          await i18nInstance.changeLanguage(data.language);
        }
      } catch (_) { /* token expired or anon — ignore */ }
    })();
    return () => { cancelled = true; };
  }, [i18nInstance]);

  // Mirror to <html lang> for a11y
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch (_) {}
  }, [lang]);

  const value = {
    t,                                  // (key) => string
    lang,                               // "bg" | "en" | …
    changeLang,                         // (code) => Promise
    setLang: changeLang,                // legacy alias (some components used setLang)
    languages: SUPPORTED_LANGUAGES,     // array for the switcher
    i18n: i18nInstance,                 // raw instance for advanced cases
  };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};

// Re-exports for callers that prefer reading constants from the context module
export { SUPPORTED_LANGUAGES, SUPPORTED_LANG_CODES, DEFAULT_LANG } from "../i18n";
// Initialise i18n on module load
export { default as i18n } from "../i18n";
